import * as z from 'zod';

export const GetStaffMemberSettingsRequest = /*#__PURE__*/ z.object({});
export const GetStaffMemberSettingsResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe(
      'ID of the staff member settings object. There is one settings object per site.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the `staffMemberSettings` object is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the `staffMemberSettings` object.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the `staffMemberSettings` object was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the `staffMemberSettings` object was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  contactInformation: z
    .object({
      publiclyAccessible: z
        .boolean()
        .describe(
          "Whether staff members' email addresses and phone numbers are accessible in API responses and on the live site.\n\n- `true`: The `email` and `phone` fields in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object) contain actual contact information, and this information is displayed on the live site.\n- `false`: These fields are empty in API responses if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions, and contact information isn't displayed on the live site.\n\nIdentities with appropriate permissions can still access this information in API responses regardless of this setting.\n\nDefault: `false`"
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Privacy settings for controlling staff member contact information visibility in API responses and on the live site.'
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the staff member settings.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.\n\nLearn more about extended fields at https://dev.wix.com/docs/rest/articles/getting-started/extended-fields.'
    )
    .optional(),
});
export const UpdateStaffMemberSettingsRequest = /*#__PURE__*/ z.object({
  staffMemberSettings: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the staff member settings object. There is one settings object per site.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the `staffMemberSettings` object is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the `staffMemberSettings` object.'
        ),
      _createdDate: z
        .date()
        .describe(
          'Date and time the `staffMemberSettings` object was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the `staffMemberSettings` object was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      contactInformation: z
        .object({
          publiclyAccessible: z
            .boolean()
            .describe(
              "Whether staff members' email addresses and phone numbers are accessible in API responses and on the live site.\n\n- `true`: The `email` and `phone` fields in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object) contain actual contact information, and this information is displayed on the live site.\n- `false`: These fields are empty in API responses if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions, and contact information isn't displayed on the live site.\n\nIdentities with appropriate permissions can still access this information in API responses regardless of this setting.\n\nDefault: `false`"
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Privacy settings for controlling staff member contact information visibility in API responses and on the live site.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the staff member settings.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.\n\nLearn more about extended fields at https://dev.wix.com/docs/rest/articles/getting-started/extended-fields.'
        )
        .optional(),
    })
    .describe('Staff member settings to update, may be partial.'),
});
export const UpdateStaffMemberSettingsResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe(
      'ID of the staff member settings object. There is one settings object per site.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the `staffMemberSettings` object is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the `staffMemberSettings` object.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the `staffMemberSettings` object was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the `staffMemberSettings` object was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  contactInformation: z
    .object({
      publiclyAccessible: z
        .boolean()
        .describe(
          "Whether staff members' email addresses and phone numbers are accessible in API responses and on the live site.\n\n- `true`: The `email` and `phone` fields in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object) contain actual contact information, and this information is displayed on the live site.\n- `false`: These fields are empty in API responses if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions, and contact information isn't displayed on the live site.\n\nIdentities with appropriate permissions can still access this information in API responses regardless of this setting.\n\nDefault: `false`"
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Privacy settings for controlling staff member contact information visibility in API responses and on the live site.'
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the staff member settings.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.\n\nLearn more about extended fields at https://dev.wix.com/docs/rest/articles/getting-started/extended-fields.'
    )
    .optional(),
});
